// Copyright 2026, Imprivata, Inc.  All rights reserved.

import { getConfigSettings } from '@api/portalServices';
import type { Setting } from '@api/types';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { useEmitOnError } from '@errorHandler/context/useEmitOnError';
import { type ApiError, AppError } from '@errorHandler/errors';
import { ImprSwitch, Select } from '@imprivata-cloud/components';
import { QueryKey } from '@portal/constants';
import {
  FaceAuthVideoStreamSettingValues,
  getSettingsTemplate,
  InstanceKey,
  LiveVideoFeedBehavior,
  StoreEnrollmentPhotosSettingValues,
  UserCanShowLiveFeedSettingValues,
} from '@portal/containers/security/constants';
import {
  buildFaceSettings,
  type FaceStored,
  isStoringEnrollmentPhotos,
  resolveLiveVideoFeedBehavior,
  useUpdateConfigSettings,
} from '@portal/containers/security/useConfigSettings';
import {
  useFaceDuplicatePreventionMutation,
  useFaceDuplicatePreventionQuery,
} from '@portal/containers/security/useFaceDuplicatePrevention';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Spin } from 'antd';
import { useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './Security.less';

const ROOT = 'portal.security';

const LIVE_FEED_OPTIONS = [
  LiveVideoFeedBehavior.HIDDEN,
  LiveVideoFeedBehavior.HIDDEN_USER_CAN_SHOW,
  LiveVideoFeedBehavior.SHOWN,
] as const;

// Edits are written as a diff against the values read here, so every visit reads once, from the
// server, and only this section's own saves read again.
const ONE_READ_PER_VISIT = {
  staleTime: 0,
  gcTime: 0,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
} as const;

type FaceChoices = {
  liveFeed: LiveVideoFeedBehavior | undefined;
  preventDuplicates: boolean;
  storePhotos: boolean;
};

type FaceEdits = {
  liveFeed?: LiveVideoFeedBehavior;
  preventDuplicates?: boolean;
  storePhotos?: boolean;
};

type Stored = { settings: FaceStored; preventDuplicates: boolean };

type FaceWrite =
  | { part: 'settings'; settings: Setting[] }
  | { part: 'duplicate-prevention'; enabled: boolean };

export type FaceSection = {
  choices: FaceChoices | undefined;
  loading: boolean;
  saving: boolean;
  dirty: boolean;
  change: (edits: FaceEdits) => void;
  undo: () => void;
  save: () => void;
};

const NO_EDITS: FaceEdits = {};

const findUserCanShowLiveFeedSetting = (settings?: Setting[]) =>
  settings?.find(item => item.name === UserCanShowLiveFeedSettingValues.name);

const findFaceAuthVideoStreamSetting = (settings?: Setting[]) =>
  settings?.find(item => item.name === FaceAuthVideoStreamSettingValues.name);

const findStoreEnrollmentPhotosSetting = (settings?: Setting[]) =>
  settings?.find(item => item.name === StoreEnrollmentPhotosSettingValues.name);

const toFaceStored = (portalSettings: Setting[], photosSetting: Setting | null): FaceStored => ({
  faceAuthVideoStreamSetting: findFaceAuthVideoStreamSetting(portalSettings),
  userCanShowLiveFeedSetting: findUserCanShowLiveFeedSetting(portalSettings),
  storeEnrollmentPhotosSetting: photosSetting ?? undefined,
});

const storedChoices = ({ settings, preventDuplicates }: Stored): FaceChoices => ({
  liveFeed: resolveLiveVideoFeedBehavior(
    settings.faceAuthVideoStreamSetting,
    settings.userCanShowLiveFeedSetting,
  ),
  preventDuplicates,
  storePhotos: isStoringEnrollmentPhotos(settings.storeEnrollmentPhotosSetting),
});

const writesFor = (stored: Stored, choices: FaceChoices): FaceWrite[] => {
  const settings = buildFaceSettings(stored.settings, {
    liveFeed: choices.liveFeed ?? null,
    storePhotos: choices.storePhotos,
  });
  const writes: FaceWrite[] = settings.length > 0 ? [{ part: 'settings', settings }] : [];
  return choices.preventDuplicates === stored.preventDuplicates
    ? writes
    : [...writes, { part: 'duplicate-prevention', enabled: choices.preventDuplicates }];
};

export function useFaceSettings(enabled: boolean): FaceSection {
  const { emitError } = useErrorContext();
  const [edits, setEdits] = useState(NO_EDITS);

  const portalSettings = useQuery<Setting[], ApiError>({
    queryKey: [QueryKey.AM_PORTAL_CONFIG_SETTINGS],
    queryFn: async () => {
      const response = await getConfigSettings({
        ...getSettingsTemplate,
        instanceKey: InstanceKey.AM_PORTAL,
      });
      return response?.settings ?? [];
    },
    enabled,
    retry: false,
    ...ONE_READ_PER_VISIT,
  });

  // store-enrollment-photos-enabled is stored with an empty instanceKey, so it is read on its own
  // rather than found in the AM Portal settings above.
  const photosSetting = useQuery<Setting | null, ApiError>({
    queryKey: [QueryKey.CONFIG_SETTINGS, StoreEnrollmentPhotosSettingValues.name],
    queryFn: async () => {
      const response = await getConfigSettings(getSettingsTemplate);
      return findStoreEnrollmentPhotosSetting(response?.settings) ?? null;
    },
    enabled,
    retry: false,
    ...ONE_READ_PER_VISIT,
  });

  const duplicates = useFaceDuplicatePreventionQuery({ ...ONE_READ_PER_VISIT, enabled });

  useEmitOnError(portalSettings.error);
  useEmitOnError(photosSetting.error);

  const updateSettings = useUpdateConfigSettings();
  const updateDuplicates = useFaceDuplicatePreventionMutation();

  const write = (next: FaceWrite) =>
    next.part === 'settings'
      ? updateSettings
          .mutateAsync(next.settings)
          .then(() =>
            Promise.all([
              portalSettings.refetch({ throwOnError: true }),
              photosSetting.refetch({ throwOnError: true }),
            ]),
          )
      : updateDuplicates.mutateAsync({ faceDuplicatePreventionEnabled: next.enabled });

  const saveEdits = useMutation({
    mutationFn: async (writes: FaceWrite[]) => {
      const results = await Promise.allSettled(writes.map(write));
      for (const [index, result] of results.entries()) {
        if (result.status === 'rejected') {
          console.error('[face-settings] save failed', writes[index].part, result.reason);
          emitError(new AppError(result.reason));
        }
      }
    },
  });

  const undo = useCallback(() => setEdits(NO_EDITS), []);

  const stored: Stored | undefined =
    portalSettings.data === undefined ||
    photosSetting.data === undefined ||
    duplicates.data === undefined
      ? undefined
      : {
          settings: toFaceStored(portalSettings.data, photosSetting.data),
          preventDuplicates: duplicates.data.faceDuplicatePreventionEnabled,
        };
  const choices = stored && { ...storedChoices(stored), ...edits };
  const writes = stored && choices ? writesFor(stored, choices) : [];

  return {
    choices,
    loading: portalSettings.isFetching || photosSetting.isFetching || duplicates.isFetching,
    saving: saveEdits.isPending,
    dirty: writes.length > 0,
    change: next => setEdits(current => ({ ...current, ...next })),
    undo,
    save: () => saveEdits.mutate(writes),
  };
}

type FaceSettingsProps = { face: FaceSection };

const FaceSettings = ({ face }: FaceSettingsProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const { choices, saving, change } = face;

  const duplicatesLabel = t(`${ROOT}.face-duplicate-prevention.label`);
  const photosLabel = t(`${ROOT}.enrollment-photos.label-on`);

  return (
    <section className="face-settings" aria-labelledby={titleId}>
      <h2 id={titleId}>{t(`${ROOT}.face.title`)}</h2>
      {choices === undefined ? (
        face.loading && <Spin />
      ) : (
        <div className="face-controls">
          {/* Figma gives the drop-down no visible label, so it is named for assistive
              technology. */}
          <Select
            aria-label={t(`${ROOT}.face.live-feed.aria-label`)}
            containerClassName="face-live-feed"
            classNames={{ popup: { root: 'face-live-feed-dropdown' } }}
            options={LIVE_FEED_OPTIONS.map(value => ({
              value,
              label: t(`${ROOT}.face.live-feed.options.${value}`),
            }))}
            value={choices.liveFeed}
            onChange={(liveFeed: LiveVideoFeedBehavior) => change({ liveFeed })}
            disabled={saving || choices.liveFeed === undefined}
          />
          <ImprSwitch
            aria-label={duplicatesLabel}
            checked={choices.preventDuplicates}
            onChange={preventDuplicates => change({ preventDuplicates })}
            disabled={saving}
            switchLabel={{ labelOn: duplicatesLabel, labelOff: duplicatesLabel }}
          />
          <ImprSwitch
            aria-label={photosLabel}
            checked={choices.storePhotos}
            onChange={storePhotos => change({ storePhotos })}
            disabled={saving}
            switchLabel={{ labelOn: photosLabel, labelOff: photosLabel }}
          />
        </div>
      )}
    </section>
  );
};

export default FaceSettings;
